import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloudSyncIcon from "@mui/icons-material/CloudSync";

import FocusTimeFilter from "./focus-time-filter";
import PatientFilter, { FilterPatientOption } from "./patient-filter";
import ShowDaysFilter from "./show-days-filter";
import TypeFilter from "./type-filter";
import SyncDialog from "./sync-dialog";

interface CalendarSidebarProps {
  visibleMeetingCount: number;
  totalMeetingCount: number;
  availablePatients: FilterPatientOption[];
  hasHiddenDayMeeting: boolean;
  hasHiddenFocusTimeMeeting: boolean;
}

const FilterHeading: React.FC<{
  label: string;
  showWarning: boolean;
  warningText: string;
}> = ({ label, showWarning, warningText }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    {showWarning && (
      <Tooltip title={warningText} arrow placement="top">
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <WarningAmberOutlinedIcon
            sx={{ fontSize: 14, color: "warning.main" }}
          />
        </Box>
      </Tooltip>
    )}
  </Stack>
);

const CalendarSidebar: React.FC<CalendarSidebarProps> = ({
  visibleMeetingCount,
  totalMeetingCount,
  availablePatients,
  hasHiddenDayMeeting,
  hasHiddenFocusTimeMeeting,
}) => {
  const [showCalendarSync, setShowCalendarSync] = useState(false);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px 0 0 8px",
        border: "1px solid #DDD",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        <Stack
          spacing={2}
          sx={{ p: 2, overflowY: "auto", flex: 1, minHeight: 0 }}
        >
          <Stack spacing={1}>
            <FilterHeading
              label="Show days"
              showWarning={hasHiddenDayMeeting}
              warningText="There are meetings on deselected days."
            />
            <ShowDaysFilter />
          </Stack>

          <Stack spacing={1}>
            <FilterHeading
              label="Focus time"
              showWarning={hasHiddenFocusTimeMeeting}
              warningText="There are meetings outside the selected focus time."
            />
            <FocusTimeFilter />
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="overline" color="text.secondary">
                Filters
              </Typography>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap" }}
              >
                {`(Showing ${visibleMeetingCount} of ${totalMeetingCount})`}
              </Typography>
            </Stack>
            <TypeFilter />
            <PatientFilter patients={availablePatients} />
          </Stack>
        </Stack>

        <Stack spacing={1.5} sx={{ p: 2, pt: 1.5 }}>
          <Button
            onClick={() => setShowCalendarSync(true)}
            startIcon={<CloudSyncIcon />}
            sx={{
              width: "100%",
              borderRadius: ".4rem",
              py: 0.5,
              fontWeight: 600,
              color: "#FFFFFF",
              borderColor: "transparent",
              background:
                "linear-gradient(90deg, #0A5CCF 0%, #1E79E8 55%, #38A1F5 100%)",
              "&:hover": {
                color: "#FFFFFF",
                borderColor: "transparent",
                background:
                  "linear-gradient(90deg, #084FAF 0%, #176AD0 55%, #2A90E0 100%)",
              },
            }}
          >
            Microsoft Outlook Sync
          </Button>
        </Stack>

        <SyncDialog
          open={showCalendarSync}
          onClose={() => setShowCalendarSync(false)}
        />
      </Stack>
    </Box>
  );
};

export default CalendarSidebar;
